import { AxiosError } from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';
import { STORAGE_KEYS } from '@/constants/storage';
import { clearAllFilterCaches } from '@/lib/filterCache';
import { clearAllAppCookies } from '@/lib/cookies';
import { getTimeUntilExpiration } from '@/utils/jwtUtils';
import { redirectToUnauthorize } from '@/utils/authRedirect';

// A 401/403 from a regular endpoint doesn't necessarily mean the session is dead — it may just
// be racing an in-flight token refresh. Before nuking the session, give the refresh a chance to
// land and retry the same request a few times; only redirect once retries are exhausted.
const AUTH_RETRY_MAX_ATTEMPTS = 3;
const AUTH_RETRY_WAIT_MS = 60000;

type RetryableConfig = InternalAxiosRequestConfig & { _authRetryCount?: number };

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

function clearAuthStorageAndRedirect() {
  try {
    localStorage.removeItem(STORAGE_KEYS.GROM_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.ACCOUNT_INFO);
    clearAllFilterCaches();
    clearAllAppCookies();
  } catch {
    // ignore storage errors
  }
  redirectToUnauthorize();
}

// Request interceptor
export const setupRequestInterceptor = (instance: AxiosInstance) => {
  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem(STORAGE_KEYS.GROM_TOKEN);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        
        // Proactive token refresh check: If token is expiring soon (< 2 minutes),
        // trigger refresh immediately (fire and forget) to prevent request failures
        try {
          const timeUntilExpiration = getTimeUntilExpiration(token);
          const twoMinutesInMs = 2 * 60 * 1000;
          
          // If token expires in less than 2 minutes, trigger refresh proactively
          if (timeUntilExpiration > 0 && timeUntilExpiration < twoMinutesInMs) {
            // Dispatch custom event to trigger token refresh in AuthContext
            // This is fire-and-forget, doesn't block the current request
            window.dispatchEvent(new CustomEvent('token:refresh-required', {
              detail: { reason: 'proactive-refresh', timeUntilExpiration }
            }));
            console.log(`Token expiring in ${Math.round(timeUntilExpiration / 1000)}s, triggering proactive refresh`);
          }
        } catch (error) {
          // If token parsing fails, silently continue - let the request proceed
          // The response interceptor will handle 401 errors
          console.warn('Failed to check token expiration in request interceptor:', error);
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

// Response interceptor
export const setupResponseInterceptor = (instance: AxiosInstance) => {
  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error: AxiosError) => {
      if (!error.response) {
        if (error.request) {
          console.error('Unable to connect to server');
        } else {
          console.error('Error:', error.message);
        }
        return Promise.reject(error instanceof Error ? error : new Error(String(error)));
      }

      const isTokenEndpoint = error.config?.url?.includes(API_ENDPOINTS.AUTH.TOKEN);

      // Token endpoint errors should be handled by AuthContext/TokenService, not interceptor
      // to avoid double navigation and conflicts. Only handle non-token endpoint errors here.
      if (isTokenEndpoint) {
        // Let the error propagate to TokenService/AuthContext for proper handling
        // They have retry logic and know the context better
        return Promise.reject(error);
      }

      const isRoleEndpoint = error.config?.url?.includes(API_ENDPOINTS.QUEUES.WITH_AGENTS_AND_ROLE);

      switch (error.response.status) {
        case 401:
        case 403: {
          // The role/queues poll retries a 401/403 locally (AppDataContext), 20s apart, up to
          // 3 attempts, before it redirects itself — don't double-handle it here.
          if (isRoleEndpoint) break;

          const config: RetryableConfig | undefined = error.config;
          const attempt = (config?._authRetryCount ?? 0) + 1;

          if (config && attempt <= AUTH_RETRY_MAX_ATTEMPTS) {
            config._authRetryCount = attempt;
            console.warn(
              `Auth error (${error.response.status}) on ${config.url}. Retrying (${attempt}/${AUTH_RETRY_MAX_ATTEMPTS}) after token refresh...`
            );
            // Kick off a silent MSAL + Grom token refresh (AuthContext's 'token:refresh-required'
            // listener), give it time to land, then retry this same request with the fresh token.
            window.dispatchEvent(new CustomEvent('token:refresh-required', { detail: { reason: 'auth-retry' } }));
            await wait(AUTH_RETRY_WAIT_MS);
            return instance(config);
          }

          // Retries exhausted (or no config to retry) → clear auth storage and redirect to unauthorize
          console.warn(`Auth error (${error.response.status}) persisted after ${AUTH_RETRY_MAX_ATTEMPTS} retries.`);
          clearAuthStorageAndRedirect();
          break;
        }
        case 404:
          console.error('Resource not found');
          break;
        default:
          // Other statuses (400, 500, etc.): log only, no navigation
          console.error('API error:', error.response?.status, error.message);
          break;
      }
      return Promise.reject(error);
    }
  );
};
